/**
 * B2B authentication API client (v2 b2bauth endpoints).
 *
 * Sign-in and reset-password use postJsonWithProblemDetails so the UI can
 * show the backend's localized error message from 401/400 ProblemDetails
 * bodies — postJson discards error bodies.
 */

import { getEnv } from "@/config/env";
import { postJson, postJsonWithProblemDetails } from "@/api/http";
import type { Result } from "@/types/layout";
import type { B2bSignInViewModel } from "@/types/b2bAccount";

const B2B_AUTH_ENDPOINT = "/b2bauth";

/**
 * Signs a B2B member in with username/password. The backend sets the session
 * cookie on success; on failure the Result error carries the localized
 * "wrong username or password" message from ProblemDetails.
 */
export async function b2bSignIn(
  username: string,
  password: string
): Promise<Result<B2bSignInViewModel>> {
  const env = getEnv();
  return postJsonWithProblemDetails<B2bSignInViewModel>(
    `${env.apiBaseUrl}${B2B_AUTH_ENDPOINT}/signin`,
    { username, password }
  );
}

/** Signs the B2B member out (clears the session cookie). Returns 204. */
export async function b2bSignOut(): Promise<Result<null>> {
  const env = getEnv();
  return postJson<null>(`${env.apiBaseUrl}${B2B_AUTH_ENDPOINT}/signout`, {});
}

/**
 * Requests a password-reset email. The backend always answers 204 (whether
 * the login exists is intentionally not observable).
 */
export async function b2bSendResetEmail(login: string): Promise<Result<null>> {
  const env = getEnv();
  return postJson<null>(
    `${env.apiBaseUrl}${B2B_AUTH_ENDPOINT}/send-reset-password-email`,
    { login }
  );
}

/**
 * Resets the password using the token from the reset email. A 400 Result
 * error carries the localized invalid/expired-token message.
 */
export async function b2bResetPassword(
  email: string,
  newPassword: string,
  token: string
): Promise<Result<null>> {
  const env = getEnv();
  return postJsonWithProblemDetails<null>(
    `${env.apiBaseUrl}${B2B_AUTH_ENDPOINT}/reset-password`,
    { email, newPassword, token }
  );
}
